import { useRef } from "react";
import { DataTable, type Column } from "../../../components/DataTable";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import type { PagAccountDetails, PagAccountReview, PagAccountRow } from "../services/pagApi";

export interface AccountReviewHistoryDialogProps {
  account: PagAccountRow | null;
  data: PagAccountDetails | null;
  loading: boolean;
  columns: Column<PagAccountReview>[];
  onClose: () => void;
}

export const AccountReviewHistoryDialog = ({
  account,
  data,
  loading,
  columns,
  onClose,
}: AccountReviewHistoryDialogProps) => {
  // Preserve the last opened account and data during the Radix UI exit animation,
  // preventing the content from resetting/collapsing to an empty state before closing.
  const lastAccountRef = useRef<PagAccountRow | null>(null);
  const lastDataRef = useRef<PagAccountDetails | null>(null);

  if (account) {
    if (
      lastAccountRef.current &&
      (lastAccountRef.current.accountName !== account.accountName ||
        lastAccountRef.current.adUserId !== account.adUserId)
    ) {
      lastDataRef.current = null;
    }
    lastAccountRef.current = account;
  }
  if (data) {
    lastDataRef.current = data;
  }

  const activeAccount = account ?? lastAccountRef.current;
  const activeData = account ? data : (lastDataRef.current ?? data);

  return (
    <Dialog
      open={account !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        className="flex max-h-[88vh] w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-[min(1560px,calc(100vw-2rem-var(--companion-rail,0px)))]"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className="gap-0.5 border-b border-border px-5 py-3 pr-12 text-left">
          <DialogTitle className="text-sm font-semibold">Review Request Details</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            {activeAccount?.accountName}
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-auto px-5 pt-3 pb-4">
          {loading && !activeData ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Loading review details
            </div>
          ) : activeData?.reviews?.length ? (
            <DataTable
              columns={columns}
              data={activeData.reviews}
              keyField="reviewId"
              compact
            />
          ) : (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No review request has been raised for this account yet.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
